import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

import TextInput from '../../components/ui/TextInput.jsx';
import GenericSelect from '../../components/ui/GenericSelect';
import AsyncGenericSelect from '../../components/ui/AsyncGenericSelect';
import Button from '../../components/ui/Button';
import DateTimePicker from '../../components/ui/DateTimePicker';
import MilestoneStepsEditor, {
  emptyMilestoneStep, fromMilestoneSteps, isMilestoneStepsValid, toMilestoneStepsPayload,
} from '../../components/ui/MilestoneStepsEditor';
import api from '../../services/api';
import GoalCheckInHistory from './GoalCheckInHistory';
import GoalTypeDetails from './GoalTypeDetails';
import { GOAL_TYPES, THRESHOLD_OPERATORS } from '../../components/performance/goalTypes';
import {
  EDITABLE_STATUSES, TRACKING_STATUS, EVALUATING_STATUS, ABANDONED_STATUS, isTerminalGoalStatus,
  weightPctToForm, weightPctToPayload, buildPendingApproverText,
} from './config';

// Skorlama Faz F, Task 5 (spec §4) — "İlerleme Geçmişi" yalnız check-in
// ÜRETİLEBİLECEK durumlarda anlamlı (draft/manager_approval/hr_approval'da
// hiç yazım olmamıştır — task-1-report.md §2: check-in'ler yalnız
// approved/evaluating'teki dönem-içi yazım yollarından üretilir); closed ve
// revision_required (approved'tan gelmiş olabilir) DAHİL — geçmiş kalıcı bir
// kayıttır, kapanış/revizyon onu SİLMEZ.
const CHECK_IN_HISTORY_STATUSES = ['approved', 'evaluating', 'closed', 'revision_required'];

const Modal = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1300;
  padding: 24px;
  overflow-y: auto;
`;

const ModalCard = styled.div`
  background: #fff;
  border-radius: 8px;
  max-width: 720px;
  width: 100%;
  padding: 24px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
  max-height: 90vh;
  overflow-y: auto;
  margin: auto;
`;

const FieldGroup = styled.div`
  margin-bottom: 16px;
`;

const Row = styled.div`
  display: grid;
  grid-template-columns: ${({ $cols }) => $cols || '1fr 1fr'};
  gap: 12px;
`;

const Actions = styled.div`
  display: flex;
  gap: 10px;
  justify-content: flex-end;
  margin-top: 20px;
  flex-wrap: wrap;
`;

const DialogTitle = styled.h3`
  margin: 0 0 16px;
  font-size: ${({ theme }) => theme.metrics.font.size.lg};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const SectionTitle = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.primary};
  margin: 20px 0 10px;
  border-bottom: 1px solid #E8ECF0;
  padding-bottom: 6px;
`;

const StatusBar = styled.div`
  padding: 10px 14px;
  background: ${({ theme }) => theme.colors.bgColor};
  border-radius: 6px;
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.primary};
  margin-bottom: 16px;
`;

// Müşteri geri bildirimi (2026-10-28) — "kimde onayda bekliyor" satırı. StatusBar'ın hemen
// altında, STATUS_COLORS.manager_approval/hr_approval'ın amber "dikkat gerektirir" tonuyla
// (#D97706/#FFFBEB — StaleBadge ile aynı aile).
const PendingApproverLine = styled.div`
  padding: 8px 14px;
  background: #FFFBEB;
  border-left: 4px solid #D97706;
  border-radius: 4px;
  font-size: 13px;
  color: #92400E;
  margin: -8px 0 16px;
`;

const RevisionBanner = styled.div`
  padding: 10px 14px;
  background: #FCE8D5;
  border-left: 4px solid #C0501A;
  border-radius: 4px;
  font-size: 13px;
  color: #7C3010;
  margin-bottom: 16px;
`;

// Skorlama Plan 4, Task 4 (task-4-brief.md) — "hedef kartında hizalı başlık
// rozeti" (AlignedOrgGoalTitle). StatusBar'ın hemen altında, TÜM salt-okunur/
// düzenlenebilir dallarda görünür (isClosed dahil — bilgilendirici, seçiciden
// bağımsız).
const AlignedOrgGoalBadge = styled.div`
  display: inline-block;
  padding: 4px 10px;
  border-radius: 12px;
  font-size: ${({ theme }) => theme.metrics.font.size.xs};
  font-weight: ${({ theme }) => theme.metrics.font.weight.semiBold};
  background: #E0E7FF;
  color: #3730A3;
  margin-bottom: 16px;
`;

const KrRow = styled.div`
  display: grid;
  grid-template-columns: ${({ $cols }) => $cols};
  gap: 10px;
  align-items: center;
  background: #F5F7FA;
  border-radius: 6px;
  padding: 10px 12px;
  margin-bottom: 8px;
`;

const RemoveBtn = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  color: #721C24;
  font-size: 16px;
  padding: 2px 8px;
  line-height: 1;
`;

const ScoreCompare = styled.div`
  font-size: 12px;
  color: #6c757d;
`;

const HintText = styled.div`
  font-size: 12px;
  color: #b02a37;
  margin-top: 4px;
`;

// P3 — soluk ipucu metni (tema token'ı; yeni alanlar için).
const MutedHint = styled.div`
  font-size: ${({ theme }) => theme.metrics.font.size.xs};
  color: ${({ theme }) => theme.colors.text.muted};
  margin-top: 4px;
`;

const ReadonlyValue = styled.div`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.primary};
  padding: 8px 0;
`;

const KPI_UNITS = ['numeric', 'currency', 'percent'];

// 1-5 puan seçenekleri — ManagerRatingPage'deki SCORE_OPTIONS deseninin aynısı
// (serbest sayı girişi yerine select: aralık dışı değer girilemez).
const SCORE_OPTIONS = [1, 2, 3, 4, 5].map((n) => ({ id: n, name: String(n) }));

// weightPct: Plan 3, Task 7 — opsiyonel ağırlık (%); dönem UsesGoalWeights=false iken
// alan hiç render edilmediği için '' olarak kalır (bkz. config weightPctToForm/Payload).
const emptyKr = () => ({
  _key: Math.random().toString(36).slice(2), id: undefined, title: '', weightPct: '', progressPct: 0, employeeScore: null, managerScore: null, managerComment: null,
});

const buildInitialForm = (goal) => {
  if (!goal) {
    return {
      goalType: 'okr',
      title: '',
      description: '',
      weightPct: '',
      kpiIndicatorName: '',
      kpiTargetValue: '',
      kpiUnit: 'numeric',
      // Skorlama Faz F, Task 4 (task-2-report.md §1) — opsiyonel, yalnız
      // GoalType=="kpi" iken payload'a girer (bkz. buildPayload).
      kpiBaselineValue: '',
      kpiCapPct: '',
      kpiDirection: 'increase',
      keyResults: [emptyKr()],
      // P3 (2026-10-08) — yeni tipler + son tarih (opsiyonel; boş = dönem bitişi).
      dueDate: null,
      milestoneSteps: [emptyMilestoneStep()],
      thresholdOperator: 'gte',
      thresholdValue: '',
    };
  }
  return {
    goalType: goal.goalType || 'okr',
    title: goal.title || '',
    description: goal.description || '',
    weightPct: weightPctToForm(goal.weightPct),
    kpiIndicatorName: goal.kpiIndicatorName || '',
    kpiTargetValue: goal.kpiTargetValue != null ? String(goal.kpiTargetValue) : '',
    kpiUnit: goal.kpiUnit || 'numeric',
    kpiBaselineValue: goal.kpiBaselineValue != null ? String(goal.kpiBaselineValue) : '',
    kpiCapPct: goal.kpiCapPct != null ? String(goal.kpiCapPct) : '',
    kpiDirection: goal.kpiDirection || 'increase',
    keyResults: (goal.keyResults && goal.keyResults.length > 0)
      ? goal.keyResults.map((k) => ({ _key: k.id, ...k, weightPct: weightPctToForm(k.weightPct) }))
      : [emptyKr()],
    dueDate: goal.dueDate ? String(goal.dueDate).slice(0, 10) : null,
    milestoneSteps: (goal.milestoneSteps && goal.milestoneSteps.length > 0)
      ? fromMilestoneSteps(goal.milestoneSteps)
      : [emptyMilestoneStep()],
    thresholdOperator: goal.thresholdOperator || 'gte',
    thresholdValue: goal.thresholdValue != null ? String(goal.thresholdValue) : '',
  };
};

// P3 — eşik sonucu taslağı (sayısal: gerçekleşen değer; ikili: evet/hayır).
const thresholdDraftOf = (goal) => ({
  value: goal?.thresholdActualValue != null ? String(goal.thresholdActualValue) : '',
  met: goal?.thresholdMet == null ? '' : (goal.thresholdMet ? 'yes' : 'no'),
});

const thresholdPayloadOf = (goal, draft) => (goal?.thresholdOperator === 'binary'
  ? { met: draft.met === '' ? undefined : draft.met === 'yes' }
  : { value: draft.value === '' ? undefined : Number(draft.value) });

const isThresholdDraftFilled = (goal, draft) => (goal?.thresholdOperator === 'binary'
  ? draft.met !== ''
  : draft.value !== '' && Number.isFinite(Number(draft.value)));

// Task 26 — Hedeflerim: OKR/KPI editörü + dönem-içi takip + dönem-sonu puanlama.
// Durum-bazlı davranış (bkz. GoalStateMachine + PeriodGoalDataAccess):
//   draft | revision_required → tam düzenlenebilir form + Onaya Gönder
//   manager_approval | hr_approval → tamamen salt-okunur (yalnız bekleme mesajı)
//   approved → başlık/KR/KPI alanları salt-okunur; KR ilerlemesi + KPI gerçekleşen
//              değeri güncellenebilir (dönem-içi takip uçları)
//   evaluating (dönem "evaluation" fazındaysa) → KR bazlı 1-5 puanlama + öz
//              değerlendirme + (varsa) yönetici puanlarıyla karşılaştırma + Kapanışı Onayla
//   closed → tamamen salt-okunur, nihai puanlar
// Revizyon isteği (GoalRevisionEnabled) ve üst hedef seçimi (CascadingGoalsEnabled)
// yalnız MEVCUT (id'li) hedeflerde ve ilgili flag açıkken gösterilir.
export default function GoalEditor({
  open, onClose, goal, periodId, periodStatus, periodUsesGoalWeights, settings, apiHooks, onSaved,
  // P3 — dönem aralığı (son tarih ve adım tarihleri dönem içinde olmalı; seçici sınırı).
  periodStartDate, periodEndDate,
}) {
  const { t } = useTranslation();
  const isNew = !goal;

  const editable = isNew || EDITABLE_STATUSES.includes(goal.status);
  const isTracking = !isNew && goal.status === TRACKING_STATUS;
  const isEvaluating = !isNew && goal.status === EVALUATING_STATUS && periodStatus === 'evaluation';
  const isPendingApproval = !isNew && ['manager_approval', 'hr_approval'].includes(goal.status);
  const isClosed = !isNew && goal.status === 'closed';
  // PMB-01 — hedef ARTIK değiştirilemez mi? "closed" VE "abandoned" (dönem
  // kapanışında hiç onaylanmamış kalan hedefin yeni terminal durumu). isClosed'ın
  // KENDİSİ genişletilmedi: o, aşağıdaki salt-okunur "kapanış özeti" bloğunu
  // (:989) sürüyor ve terk edilmiş hedefte o özet GÖRÜNMEMELİ (hedef kapanmadı,
  // terk edildi). Bu bayrak yalnız "canlı kontrol göster/gizle" kararları için.
  const isTerminal = !isNew && isTerminalGoalStatus(goal.status);
  // Müşteri geri bildirimi (2026-10-28) — aşağıdaki durum dallarının HİÇBİRİNE düşmeyen hedef
  // (terk edilmiş; ya da "evaluating" ama dönem artık "evaluation" değil — kalibrasyon/
  // pending_close) eskiden yalnız başlık + durum çubuğu olan bir pencere açıyordu: Kapat düğmesi
  // olmadığı için tam ekran diyalog KAPATILAMIYORDU. hasStateBranch=false salt-okunur özet +
  // Kapat dalını açar — her durumun bir çıkışı olur.
  const hasStateBranch = editable || isPendingApproval || isTracking || isEvaluating || isClosed;
  // Müşteri geri bildirimi (2026-10-28) — "kimde onayda bekliyor" (yalnız tekil okumada dolu;
  // bkz. config buildPendingApproverText).
  const pendingApproverText = !isNew ? buildPendingApproverText(goal, t) : null;

  const [form, setForm] = useState(() => buildInitialForm(goal));
  useEffect(() => { setForm(buildInitialForm(goal)); }, [goal]);

  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Dönem-içi takip (approved)
  const [kpiActualDraft, setKpiActualDraft] = useState(goal?.kpiActualValue != null ? String(goal.kpiActualValue) : '');
  const [krProgressDraft, setKrProgressDraft] = useState({});
  const [trackingBusy, setTrackingBusy] = useState(false);
  // Skorlama Faz F, Task 5 (task-1-report.md §1) — opsiyonel check-in notu;
  // deviationRationaleDraft/selfAssessmentDraft ile AYNI "tek paylaşılan
  // taslak alanı" deseni (goal başına TEK not, KR bazlı DEĞİL — birden çok
  // KR aynı "Kaydet" tıklamasında güncellenirse hepsi AYNI notu taşır).
  const [progressNoteDraft, setProgressNoteDraft] = useState('');
  // P3 — eşik/ikili sonuç taslağı (dönem içi + dönem sonu ortak).
  const [thresholdDraft, setThresholdDraft] = useState(() => thresholdDraftOf(goal));
  useEffect(() => { setThresholdDraft(thresholdDraftOf(goal)); }, [goal]);
  useEffect(() => {
    setKpiActualDraft(goal?.kpiActualValue != null ? String(goal.kpiActualValue) : '');
    const init = {};
    (goal?.keyResults || []).forEach((k) => { init[k.id] = k.progressPct ?? 0; });
    setKrProgressDraft(init);
    setProgressNoteDraft('');
  }, [goal]);

  // Dönem sonu puanlama (evaluating)
  const [employeeScoresDraft, setEmployeeScoresDraft] = useState({});
  const [selfAssessmentDraft, setSelfAssessmentDraft] = useState(goal?.selfAssessmentText || '');
  // P1 — sapma gerekçesi (opsiyonel): boşaltılıp kaydedilirse BE '' →
  // temizleme olarak işler (bkz. config submitEmployeeScores yorumu).
  const [deviationRationaleDraft, setDeviationRationaleDraft] = useState(goal?.deviationRationale || '');
  const [scoringBusy, setScoringBusy] = useState(false);
  const [closureBusy, setClosureBusy] = useState(false);
  useEffect(() => {
    const init = {};
    (goal?.keyResults || []).forEach((k) => { init[k.id] = k.employeeScore ?? null; });
    setEmployeeScoresDraft(init);
    setSelfAssessmentDraft(goal?.selfAssessmentText || '');
    setDeviationRationaleDraft(goal?.deviationRationale || '');
  }, [goal]);

  // Revizyon isteği (flag)
  const [revisionOpen, setRevisionOpen] = useState(false);
  const [revisionForm, setRevisionForm] = useState(null);
  const [revisionBusy, setRevisionBusy] = useState(false);

  // Üst hedef seçimi (flag) — GET /my `title[like]` ile sunucu tarafında aranır
  // (bkz. aşağıdaki parentSearchProps).
  const [parentGoalId, setParentGoalId] = useState(goal?.parentGoalId || null);
  const [parentBusy, setParentBusy] = useState(false);
  useEffect(() => { setParentGoalId(goal?.parentGoalId || null); }, [goal]);

  // Skorlama Plan 4, Task 4 (task-4-brief.md) — "Üst Hedefe Hizala" (OrgGoal
  // hizalaması). Bayraksız (settings flag'i YOK — task-3-report.md'de flag
  // kontrolü yok) — yalnız goal varlığı/terminal durumu koşullu (bkz. render'daki
  // !isNew && !isTerminal — backend SetAlignedOrgGoalAsync kapanmış dönemde her
  // yazmayı reddeder; PMB-01'den sonra terminal küme "closed" + "abandoned").
  const [alignedOrgGoalId, setAlignedOrgGoalId] = useState(goal?.alignedOrgGoalId || null);
  const [alignBusy, setAlignBusy] = useState(false);
  useEffect(() => { setAlignedOrgGoalId(goal?.alignedOrgGoalId || null); }, [goal]);

  const krKeyCounter = useRef(0);

  // Skorlama Faz F, Task 4 (spec §4, task-3-report.md §1.4) — "Şablondan
  // başla" seçici, YALNIZ yeni-hedef modunda. AuthContext'in `user` nesnesi
  // departmanId/positionId TAŞIMIYOR (grep ile doğrulandı — hiçbir sayfa
  // `user.departmentId`/`user.positionId` okumuyor, UserDetailResponse'ta da
  // yok), bu yüzden task-4-brief.md'nin açık fallback'i uygulanır: FİLTRESİZ
  // çağrılır — BE boş filtrede TÜM kütüphaneyi döner (küçük kütüphane
  // varsayımı, düz dizi — task-3-report.md concern #1, `.data.data` DEĞİL).
  // try/catch: GoalEditor.test.js'in çoğu senaryosu `api.get`'i hiç mock'lamaz
  // (mock modülü varsayılan `{}`) — bu istek başarısız/undefined olursa
  // sessizce boş listeye düşer, seçici gizli kalır (aşağıdaki templateOptions).
  const [templates, setTemplates] = useState([]);
  useEffect(() => {
    if (!isNew) { setTemplates([]); return undefined; }
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get('/v1/goal-templates');
        const items = res.data?.data;
        if (!cancelled) setTemplates(Array.isArray(items) ? items : []);
      } catch {
        if (!cancelled) setTemplates([]);
      }
    })();
    return () => { cancelled = true; };
  }, [isNew]);

  const [templateId, setTemplateId] = useState('');
  const templateOptions = useMemo(
    () => templates.map((tpl) => ({ id: tpl.id, name: tpl.name })),
    [templates],
  );

  // Seçimde tüm alanları doldurur (tip/başlık/açıklama/KR başlıkları/KPI
  // alanları — task-4-brief.md); sonrası TAMAMEN serbest düzenleme (bir daha
  // hiçbir alan şablona kilitlenmez, tekrar seçilirse aynı alanlar yeniden
  // ezilir). KR başlığı olmayan bir OKR şablonu tek boş satıra düşer (mevcut
  // emptyKr() varsayımı, formun her zaman en az bir KR satırı taşıması
  // gerekliliğiyle parite).
  const applyTemplate = useCallback((id) => {
    setTemplateId(id);
    const tpl = templates.find((x) => x.id === id);
    if (!tpl) return;
    setForm((prev) => ({
      ...prev,
      goalType: tpl.goalType,
      title: tpl.title || '',
      description: tpl.description || '',
      kpiIndicatorName: tpl.kpiIndicatorName || '',
      kpiTargetValue: tpl.kpiTargetValue != null ? String(tpl.kpiTargetValue) : '',
      kpiUnit: tpl.kpiUnit || 'numeric',
      kpiBaselineValue: tpl.kpiBaselineValue != null ? String(tpl.kpiBaselineValue) : '',
      kpiCapPct: tpl.kpiCapPct != null ? String(tpl.kpiCapPct) : '',
      // Yönsüz eski şablonda BE'nin çıkarımıyla AYNI kural: baz > hedef → azalış.
      // (Aksi halde form "Artış" gösterip kaydı directionBaselineMismatch'e düşürürdü.)
      kpiDirection: tpl.kpiDirection
        || (tpl.kpiBaselineValue != null && tpl.kpiTargetValue != null
            && Number(tpl.kpiBaselineValue) > Number(tpl.kpiTargetValue)
          ? 'decrease' : 'increase'),
      keyResults: (tpl.krTitles && tpl.krTitles.length > 0)
        ? tpl.krTitles.map((title) => ({ ...emptyKr(), title }))
        : [emptyKr()],
      // P3 — kilometre taşı adımları / eşik koşulu şablondan (kısmi şablon olabilir).
      milestoneSteps: (tpl.milestoneSteps && tpl.milestoneSteps.length > 0)
        ? fromMilestoneSteps(tpl.milestoneSteps)
        : [emptyMilestoneStep()],
      thresholdOperator: tpl.thresholdOperator || 'gte',
      thresholdValue: tpl.thresholdValue != null ? String(tpl.thresholdValue) : '',
    }));
  }, [templates]);

  const handleFieldChange = useCallback((field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  }, []);

  const addKr = useCallback(() => {
    krKeyCounter.current += 1;
    setForm((prev) => ({ ...prev, keyResults: [...prev.keyResults, { ...emptyKr(), _key: `new-${krKeyCounter.current}` }] }));
  }, []);

  const removeKr = useCallback((idx) => {
    setForm((prev) => {
      const next = [...prev.keyResults];
      next.splice(idx, 1);
      return { ...prev, keyResults: next };
    });
  }, []);

  const updateKrTitle = useCallback((idx, value) => {
    setForm((prev) => {
      const next = [...prev.keyResults];
      next[idx] = { ...next[idx], title: value };
      return { ...prev, keyResults: next };
    });
  }, []);

  // Plan 3, Task 7 — KR satırı ağırlığı (yalnız periodUsesGoalWeights true iken render edilir).
  const updateKrWeight = useCallback((idx, value) => {
    setForm((prev) => {
      const next = [...prev.keyResults];
      next[idx] = { ...next[idx], weightPct: value };
      return { ...prev, keyResults: next };
    });
  }, []);

  const krTitles = useMemo(
    () => form.keyResults.map((k) => (k.title || '').trim()).filter(Boolean),
    [form.keyResults],
  );

  // P3 — tip başına gönderilebilirlik (backend kapılarının istemci ikizi).
  const thresholdFormValid = (form.kpiIndicatorName || '').trim() !== ''
    && THRESHOLD_OPERATORS.includes(form.thresholdOperator)
    && (form.thresholdOperator === 'binary' || (form.thresholdValue !== '' && Number.isFinite(Number(form.thresholdValue))));
  const milestoneFormValid = isMilestoneStepsValid(form.milestoneSteps);
  const canSubmit = form.goalType === 'okr' ? krTitles.length > 0
    : form.goalType === 'milestone' ? milestoneFormValid
      : form.goalType === 'threshold' ? thresholdFormValid
        : true;
  // Kayıt kapısı: kilometre taşında ağırlık toplamı 100 değilse backend kaydı reddeder — düğme kilitlenir.
  const canSave = !!form.title.trim() && (form.goalType !== 'milestone' || milestoneFormValid);

  const buildPayload = useCallback(() => ({
    periodId: goal?.periodId || periodId,
    goalType: form.goalType,
    title: form.title,
    description: form.description || undefined,
    // Plan 3, Task 7 — bayraksız dönemde alan hiç render edilmediği için form.weightPct
    // hep '' kalır → weightPctToPayload undefined döner (parite, BE de null'ı kabul eder).
    weightPct: weightPctToPayload(form.weightPct),
    // P3 — eşik hedefi gösterge adını ve birimini KPI alanlarıyla paylaşır (ikili koşulda birim yok).
    kpiIndicatorName: form.goalType === 'kpi' || form.goalType === 'threshold' ? form.kpiIndicatorName : undefined,
    kpiTargetValue: form.goalType === 'kpi' && form.kpiTargetValue !== '' ? Number(form.kpiTargetValue) : undefined,
    kpiUnit: form.goalType === 'kpi' || (form.goalType === 'threshold' && form.thresholdOperator !== 'binary') ? form.kpiUnit : undefined,
    // Skorlama Faz F, Task 4 (task-2-report.md §1) — opsiyonel, KpiTargetValue
    // ile AYNI desen (yalnız kpi tipinde ve doluysa gönderilir).
    kpiBaselineValue: form.goalType === 'kpi' && form.kpiBaselineValue !== '' ? Number(form.kpiBaselineValue) : undefined,
    kpiCapPct: form.goalType === 'kpi' && form.kpiCapPct !== '' ? Number(form.kpiCapPct) : undefined,
    kpiDirection: form.goalType === 'kpi' ? form.kpiDirection : undefined,
    keyResults: form.goalType === 'okr'
      ? form.keyResults.filter((k) => (k.title || '').trim()).map((k) => ({ title: k.title.trim(), weightPct: weightPctToPayload(k.weightPct) }))
      : undefined,
    // P3 — son tarih (boş = dönem bitişi), kilometre taşı adımları, eşik koşulu.
    dueDate: form.dueDate || undefined,
    milestoneSteps: form.goalType === 'milestone' ? toMilestoneStepsPayload(form.milestoneSteps) : undefined,
    thresholdOperator: form.goalType === 'threshold' ? form.thresholdOperator : undefined,
    thresholdValue: form.goalType === 'threshold' && form.thresholdOperator !== 'binary' && form.thresholdValue !== ''
      ? Number(form.thresholdValue) : undefined,
    // Kullanıcı tipi bilerek değiştirdi: sunucu kilometre taşı/eşik hedefinin tip değişimini yalnız bu onayla kabul eder.
    confirmTypeChange: goal && goal.goalType !== form.goalType ? true : undefined,
  }), [form, goal, periodId]);

  const handleSave = useCallback(async () => {
    if (!canSave) return;
    setSaving(true);
    const payload = buildPayload();
    const ok = goal ? await apiHooks.updateDraft(goal.id, payload) : await apiHooks.createDraft(payload);
    setSaving(false);
    if (ok) onSaved();
  }, [canSave, goal, buildPayload, apiHooks, onSaved]);

  // Önce mevcut form taslağını kaydeder (buton yan yana — kullanıcı "Kaydet"e hiç
  // basmadan doğrudan "Onaya Gönder"e basarsa yaptığı değişikliklerin sessizce
  // kaybolmaması için), sonra submit eder. Kaydetme başarısız olursa submit hiç
  // denenmez (interceptor zaten hata toast'ı gösterdi).
  const handleSubmit = useCallback(async () => {
    if (!goal || !canSubmit || !canSave) return;
    setSubmitting(true);
    const saved = await apiHooks.updateDraft(goal.id, buildPayload());
    const ok = saved && await apiHooks.submitGoal(goal.id);
    setSubmitting(false);
    if (ok) onSaved();
  }, [goal, canSubmit, canSave, buildPayload, apiHooks, onSaved]);

  const handleSaveTracking = useCallback(async () => {
    if (!goal) return;
    setTrackingBusy(true);
    // Skorlama Faz F, Task 5 — check-in notu, bu "Kaydet" tıklamasında
    // gerçekten yazılan HER değişikliğe eşlik eder (KR satır(lar)ı ve/veya
    // KPI gerçekleşen). Değer değişmeden yalnız not girilmesi bu akışta
    // bağımsız bir yazım TETİKLEMEZ (KR döngüsü hâlâ "değişti mi" kapısını
    // korur — birden çok KR'de gereksiz tekrar çağrı önlenir, mevcut davranış);
    // bu bilinçli bir kapsam sınırıdır (task-5-report.md'de not düşülmüştür).
    const note = progressNoteDraft.trim() || undefined;
    let ok = true;
    if (goal.goalType === 'okr') {
      for (const kr of goal.keyResults) {
        const v = krProgressDraft[kr.id];
        if (v === undefined) continue;
        // Girdide min/max var ama elle yazılan taşmalar (ör. 150 veya -5)
        // API'ye gitmeden 0-100 aralığına kırpılır; sayı olmayan girdi atlanır.
        const clamped = Math.min(100, Math.max(0, Number(v)));
        if (!Number.isFinite(clamped)) continue;
        if (clamped !== Number(kr.progressPct ?? 0)) {
          // eslint-disable-next-line no-await-in-loop
          const rowOk = await apiHooks.updateKrProgress(kr.id, clamped, note);
          ok = ok && rowOk;
        }
      }
    } else if (goal.goalType === 'threshold') {
      // P3 — eşik/ikili dönem içi sonuç (check-in notuyla).
      if (isThresholdDraftFilled(goal, thresholdDraft)) {
        ok = await apiHooks.updateThresholdResult(goal.id, { ...thresholdPayloadOf(goal, thresholdDraft), note });
      }
    } else if (goal.goalType === 'kpi' && kpiActualDraft !== '') {
      // Kilometre taşının dönem içi ilerlemesi yalnız adım kabulüyle değişir (GoalTypeDetails) — burada yazım yok.
      ok = await apiHooks.updateKpiActual(goal.id, Number(kpiActualDraft), note);
    }
    setTrackingBusy(false);
    if (ok) onSaved();
  }, [goal, krProgressDraft, kpiActualDraft, thresholdDraft, progressNoteDraft, apiHooks, onSaved]);

  const handleSaveScores = useCallback(async () => {
    if (!goal) return;
    setScoringBusy(true);
    const scores = (goal.keyResults || [])
      .filter((k) => employeeScoresDraft[k.id] !== '' && employeeScoresDraft[k.id] != null)
      .map((k) => ({ keyResultId: k.id, score: Number(employeeScoresDraft[k.id]) }));
    // P1 — sapma gerekçesi her iki gönderime de eşlik eder ('' = temizleme,
    // BE null=dokunma/boş=temizle semantiği).
    // PMB-48 — `selfAssessmentDraft || undefined` KALDIRILDI. Backend artık
    // null'ı "dokunma" olarak okuyor; alan boş bırakıldığında `undefined`
    // göndermek (JSON'dan düşer → BE'de null) çalışanın metni SİLME niyetini
    // sessizce yutardı. Taslak ham gönderilir: '' = bilinçli temizleme,
    // deviationRationaleDraft'ın bir satır aşağıdaki davranışının AYNISI.
    let ok = await apiHooks.submitEmployeeScores(
      goal.id, scores, selfAssessmentDraft, deviationRationaleDraft,
    );
    if (ok && goal.goalType === 'kpi' && kpiActualDraft !== '') {
      ok = await apiHooks.submitFinalKpi(goal.id, Number(kpiActualDraft), deviationRationaleDraft);
    }
    // P3 — eşik/ikili dönem sonu final sonuç.
    if (ok && goal.goalType === 'threshold' && isThresholdDraftFilled(goal, thresholdDraft)) {
      ok = await apiHooks.submitFinalThreshold(goal.id, { ...thresholdPayloadOf(goal, thresholdDraft), deviationRationale: deviationRationaleDraft });
    }
    setScoringBusy(false);
    if (ok) onSaved();
  }, [goal, employeeScoresDraft, selfAssessmentDraft, deviationRationaleDraft, kpiActualDraft, thresholdDraft, apiHooks, onSaved]);

  const canApproveClosure = useMemo(() => {
    if (!goal) return false;
    if (goal.goalType === 'kpi') return goal.kpiActualValue != null;
    // P3 — kilometre taşı: adım var ve kabul bekleyen adım yok; eşik: sonuç girilmiş.
    if (goal.goalType === 'milestone') {
      const steps = goal.milestoneSteps || [];
      return steps.length > 0 && steps.every((st) => st.status !== 'submitted');
    }
    if (goal.goalType === 'threshold') return goal.thresholdConditionMet != null;
    return (goal.keyResults || []).every((k) => k.managerScore != null);
  }, [goal]);

  // P3 — kapanış onayının neden kilitli olduğu (tip başına ipucu).
  const closureHintKey = goal?.goalType === 'milestone' ? 'performance.goal.milestone.waitingDecisions'
    : goal?.goalType === 'threshold' ? 'performance.goal.threshold.resultRequired'
      : 'performance.goal.waitingManagerScores';

  const handleApproveClosure = useCallback(async () => {
    if (!goal || !canApproveClosure) return;
    setClosureBusy(true);
    const ok = await apiHooks.approveClosure(goal.id);
    setClosureBusy(false);
    if (ok) onSaved();
  }, [goal, canApproveClosure, apiHooks, onSaved]);

  const openRevision = useCallback(() => {
    setRevisionForm({
      title: goal.title,
      description: goal.description || '',
      // Plan 3, Task 7 — bkz. WeightPct revizyon alanı aşağıda; yalnız periodUsesGoalWeights
      // true iken render edilir (bayraksız dönemde goal.weightPct zaten hep null'dır).
      weightPct: weightPctToForm(goal.weightPct),
      kpiTargetValue: goal.kpiTargetValue != null ? String(goal.kpiTargetValue) : '',
      // Skorlama Faz F, Task 4 (task-2-report.md §5) — revizyon beyaz
      // listesine eklenen iki yeni alan, KpiTargetValue ile AYNI opsiyonel
      // desen (bkz. handleRequestRevision).
      kpiBaselineValue: goal.kpiBaselineValue != null ? String(goal.kpiBaselineValue) : '',
      kpiCapPct: goal.kpiCapPct != null ? String(goal.kpiCapPct) : '',
      kpiDirection: goal.kpiDirection || 'increase',
      krTitles: Object.fromEntries((goal.keyResults || []).map((k) => [k.id, k.title])),
      // P3 — son tarih, kilometre taşı adımları (başlık/ağırlık/son tarih), eşik koşulu.
      dueDate: goal.dueDate ? String(goal.dueDate).slice(0, 10) : null,
      steps: Object.fromEntries((goal.milestoneSteps || []).map((st) => [st.id, {
        title: st.title, weightPct: String(st.weightPct), dueDate: st.dueDate ? String(st.dueDate).slice(0, 10) : null,
      }])),
      thresholdOperator: goal.thresholdOperator || 'gte',
      thresholdValue: goal.thresholdValue != null ? String(goal.thresholdValue) : '',
    });
    setRevisionOpen(true);
  }, [goal]);

  const handleRequestRevision = useCallback(async () => {
    if (!goal || !revisionForm) return;
    const changes = {};
    if (revisionForm.title.trim() && revisionForm.title.trim() !== goal.title) changes.Title = revisionForm.title.trim();
    if ((revisionForm.description || '') !== (goal.description || '')) changes.Description = revisionForm.description || '';
    if (goal.goalType === 'kpi' && revisionForm.kpiTargetValue !== '' && Number(revisionForm.kpiTargetValue) !== Number(goal.kpiTargetValue)) {
      changes.KpiTargetValue = revisionForm.kpiTargetValue;
    }
    // Skorlama Faz F, Task 4 (task-2-report.md §5) — "KpiBaselineValue"/
    // "KpiCapPct" ChangeJson anahtarları, KpiTargetValue ile BİREBİR aynı
    // opsiyonel-değişti-mi deseni (boş bırakılırsa hiç gönderilmez — BE
    // boş string'i parse edemeyip revisionFieldNotAllowed döner, bu yüzden
    // "temizleme" bu kanaldan desteklenmiyor, yalnız SET/DEĞİŞTİR).
    if (goal.goalType === 'kpi' && revisionForm.kpiBaselineValue !== '' && Number(revisionForm.kpiBaselineValue) !== Number(goal.kpiBaselineValue)) {
      changes.KpiBaselineValue = revisionForm.kpiBaselineValue;
    }
    if (goal.goalType === 'kpi' && revisionForm.kpiCapPct !== '' && Number(revisionForm.kpiCapPct) !== Number(goal.kpiCapPct)) {
      changes.KpiCapPct = revisionForm.kpiCapPct;
    }
    // Hedef yönü (2026-08-10) — beyaz listedeki "KpiDirection"; yalnız gerçekten
    // değiştiyse gönderilir (mevcut alanların değişti-mi deseniyle aynı).
    if (goal.goalType === 'kpi' && revisionForm.kpiDirection && revisionForm.kpiDirection !== (goal.kpiDirection || 'increase')) {
      changes.KpiDirection = revisionForm.kpiDirection;
    }
    // Plan 3, Task 6/7 — ChangeJson anahtarı PascalCase "WeightPct" (BE
    // BuildRevisionChangeSetAsync ile birebir); yalnız hedef ağırlığı revize edilebilir,
    // KR ağırlığı revizyon kapsamı DIŞINDA (BE yalnız "kr:<id>" başlığını destekliyor).
    if (periodUsesGoalWeights && revisionForm.weightPct !== '' && Number(revisionForm.weightPct) !== Number(goal.weightPct)) {
      changes.WeightPct = revisionForm.weightPct;
    }
    (goal.keyResults || []).forEach((k) => {
      const newTitle = revisionForm.krTitles[k.id];
      if (newTitle && newTitle.trim() && newTitle.trim() !== k.title) changes[`kr:${k.id}`] = newTitle.trim();
    });
    // P3 (2026-10-08) — ChangeJson anahtarları backend beyaz listesiyle birebir: DueDate,
    // step:/stepWeight:/stepDue:<id>, ThresholdOperator, ThresholdValue.
    const oldDue = goal.dueDate ? String(goal.dueDate).slice(0, 10) : null;
    if (revisionForm.dueDate && revisionForm.dueDate !== oldDue) changes.DueDate = revisionForm.dueDate;
    if (goal.goalType === 'milestone') {
      (goal.milestoneSteps || []).forEach((st) => {
        const draft = revisionForm.steps[st.id];
        if (!draft) return;
        if (draft.title && draft.title.trim() && draft.title.trim() !== st.title) changes[`step:${st.id}`] = draft.title.trim();
        if (draft.weightPct !== '' && Number(draft.weightPct) !== Number(st.weightPct)) changes[`stepWeight:${st.id}`] = String(draft.weightPct);
        const oldStepDue = st.dueDate ? String(st.dueDate).slice(0, 10) : null;
        if ((draft.dueDate || null) !== oldStepDue) changes[`stepDue:${st.id}`] = draft.dueDate || '';
      });
    }
    if (goal.goalType === 'threshold') {
      if (revisionForm.thresholdOperator && revisionForm.thresholdOperator !== goal.thresholdOperator) {
        changes.ThresholdOperator = revisionForm.thresholdOperator;
      }
      if (revisionForm.thresholdOperator !== 'binary' && revisionForm.thresholdValue !== ''
        && Number(revisionForm.thresholdValue) !== Number(goal.thresholdValue)) {
        changes.ThresholdValue = String(revisionForm.thresholdValue);
      }
    }
    if (Object.keys(changes).length === 0) return;
    setRevisionBusy(true);
    const ok = await apiHooks.requestRevision(goal.id, changes);
    setRevisionBusy(false);
    if (ok) {
      setRevisionOpen(false);
      onSaved();
    }
  }, [goal, revisionForm, periodUsesGoalWeights, apiHooks, onSaved]);

  // Üst hedef araması — GET /v1/period-goals/my `title[like]` bind ediyor
  // (PeriodGoalController.GetMy); önceden yalnız YÜKLÜ sayfadaki hedefler
  // (myGoals prop'u) client-side aranıyordu, ilk sayfada olmayan hedef hiç
  // bulunamıyordu. Aynı dönemle sınırlı sunucu araması; hedefin kendisi elenir.
  const parentSearchProps = useMemo(() => ({
    onSearch: async (q) => {
      const params = {
        'periodId[eq]': goal?.periodId || periodId,
        pageSize: 20,
        ...(q ? { 'title[like]': q } : {}),
      };
      const res = await api.get('/v1/period-goals/my', { params });
      const r = res.data?.data;
      const items = Array.isArray(r) ? r : (r?.data || []);
      return items
        .filter((g) => g.id !== goal?.id)
        .map((g) => ({ id: g.id, name: g.title }));
    },
    onGetById: async (id) => {
      if (!id) return null;
      const res = await api.get(`/v1/period-goals/my/${id}`);
      const g = res.data?.data;
      return g ? { id: g.id, name: g.title } : { id, name: id };
    },
  }), [goal, periodId]);

  const handleSaveParent = useCallback(async () => {
    if (!goal) return;
    setParentBusy(true);
    const ok = await apiHooks.setParent(goal.id, parentGoalId || null);
    setParentBusy(false);
    if (ok) onSaved();
  }, [goal, parentGoalId, apiHooks, onSaved]);

  // Skorlama Plan 4, Task 4 — kurumsal hedef (OrgGoal) araması, parentSearchProps'un
  // BİREBİR aynı deseni: GET v1/period-goals/org-goal-options?periodId= (task-3-report.md'de
  // doğrulanmış gerçek uç — brief'teki "v1/org-goals/options" BAYAT). Uç arama/filtre
  // parametresi DESTEKLEMEDİĞİ için (OrgGoalDataAccess.GetOptionsAsync yalnız periodId
  // alır) istemci tarafında başlığa göre filtrelenir — küçük, dönem-başına bir liste
  // (yalnız Status=="active" org hedefleri).
  const alignedOrgGoalSearchProps = useMemo(() => {
    const effectivePeriodId = goal?.periodId || periodId;
    const label = (o) => `${o.title} (${t(`performance.orgGoals.ownerType.${o.ownerType}`, { defaultValue: o.ownerType })})`;
    return {
      onSearch: async (q) => {
        const res = await api.get('/v1/period-goals/org-goal-options', { params: { periodId: effectivePeriodId } });
        const items = res.data?.data || [];
        const filtered = q
          ? items.filter((o) => o.title.toLocaleLowerCase('tr-TR').includes(q.toLocaleLowerCase('tr-TR')))
          : items;
        return filtered.map((o) => ({ id: o.id, name: label(o) }));
      },
      onGetById: async (id) => {
        if (!id) return null;
        const res = await api.get('/v1/period-goals/org-goal-options', { params: { periodId: effectivePeriodId } });
        const items = res.data?.data || [];
        const found = items.find((o) => o.id === id);
        return found ? { id: found.id, name: label(found) } : { id, name: id };
      },
    };
  }, [goal, periodId, t]);

  const handleSaveAlignedOrgGoal = useCallback(async () => {
    if (!goal) return;
    setAlignBusy(true);
    const ok = await apiHooks.setAlignedOrgGoal(goal.id, alignedOrgGoalId || null);
    setAlignBusy(false);
    if (ok) onSaved();
  }, [goal, alignedOrgGoalId, apiHooks, onSaved]);

  const kpiUnitOptions = useMemo(() => KPI_UNITS.map((u) => ({ id: u, name: t(`performance.goal.kpiUnit.${u}`) })), [t]);
  // P3 — tip ve eşik operatörü seçenekleri (tek kaynak GoalTypeDetails sabitleri).
  const goalTypeOptions = useMemo(() => GOAL_TYPES.map((gt) => ({ id: gt, name: t(`performance.goal.goalType.${gt}`) })), [t]);
  const thresholdOperatorOptions = useMemo(() => THRESHOLD_OPERATORS.map((op) => ({ id: op, name: t(`performance.goal.threshold.operator.${op}`) })), [t]);
  const binaryOptions = useMemo(() => [
    { id: 'yes', name: t('performance.goal.threshold.yes') },
    { id: 'no', name: t('performance.goal.threshold.no') },
  ], [t]);
  const minDay = periodStartDate ? String(periodStartDate).slice(0, 10) : undefined;
  const maxDay = periodEndDate ? String(periodEndDate).slice(0, 10) : undefined;
  // Adım "tamamlandı" yalnız onaylı/değerlendirmedeki hedefte (backend kapısıyla aynı).
  const canActOnSteps = !isNew && ['approved', 'evaluating'].includes(goal.status);
  const reload = onSaved;

  // Eşik sonucu girişi (dönem içi + dönem sonu ortak alan).
  const thresholdResultField = goal?.goalType === 'threshold' && (goal.thresholdOperator === 'binary' ? (
    <FieldGroup>
      <GenericSelect
        label={t('performance.goal.threshold.resultLabel')}
        value={thresholdDraft.met}
        onChange={(v) => setThresholdDraft((prev) => ({ ...prev, met: v || '' }))}
        options={binaryOptions}
        idKey="id"
        labelKey="name"
      />
    </FieldGroup>
  ) : (
    <FieldGroup>
      <TextInput
        label={t('performance.goal.threshold.actual')}
        type="number"
        value={thresholdDraft.value}
        onChange={(e) => setThresholdDraft((prev) => ({ ...prev, value: e.target.value }))}
        data-role="threshold-actual-input"
      />
    </FieldGroup>
  ));
  const kpiDirectionOptions = useMemo(() => ['increase', 'decrease'].map((d) => ({ id: d, name: t(`performance.goal.kpiDirection.${d}`) })), [t]);

  // Müşteri geri bildirimi (2026-10-28) — "hedefin ağırlığı gözükmeli": salt-okunur dallarda
  // ağırlık, doluysa gösterilir (ağırlık yalnız usesGoalWeights döneminde yazılabildiği için
  // dolu olması bayrağın kendisidir — ayrı dönem koşulu gerekmez). Liste kolonuyla aynı biçim.
  const weightReadonly = !isNew && goal.weightPct != null ? (
    <ReadonlyValue data-role="goal-weight-readonly">
      {t('performance.goal.fields.weight')}: {goal.weightPct}%
    </ReadonlyValue>
  ) : null;

  if (!open) return null;

  return (
    <Modal>
      <ModalCard>
        <DialogTitle>{goal ? t('performance.goal.edit') : t('performance.goal.new')}</DialogTitle>

        {!isNew && (
          <StatusBar>
            {t('common.status')}: {t(`performance.goal.status.${goal.status}`, { defaultValue: goal.status })}
          </StatusBar>
        )}

        {pendingApproverText && (
          <PendingApproverLine data-role="pending-approver">{pendingApproverText}</PendingApproverLine>
        )}

        {/* Skorlama Plan 4, Task 4 — "hedef kartında hizalı başlık rozeti"
            (task-4-brief.md). Bilgilendirici, seçiciden BAĞIMSIZ — isClosed
            dahil TÜM dallarda görünür (org hedef başlıkları kamusal, spec §7). */}
        {!isNew && goal.alignedOrgGoalTitle && (
          <AlignedOrgGoalBadge data-role="aligned-org-goal-badge">
            {t('performance.goal.alignedOrgGoalBadge')}: {goal.alignedOrgGoalTitle}
          </AlignedOrgGoalBadge>
        )}

        {!isNew && goal.status === 'revision_required' && goal.approvalNote && (
          <RevisionBanner>
            <strong>{t('performance.goal.revisionNote')}:</strong> {goal.approvalNote}
          </RevisionBanner>
        )}

        {editable && (
          <>
            {/* Skorlama Faz F, Task 4 (spec §4) — "Şablondan başla". YALNIZ
                yeni-hedef modunda (isNew) VE kütüphane boş DEĞİLSE (brief:
                "boş listede seçici GİZLİ") gösterilir. */}
            {isNew && templateOptions.length > 0 && (
              <>
                <SectionTitle>{t('performance.goal.templateSelectorLabel')}</SectionTitle>
                <HintText style={{ color: '#6c757d' }}>{t('performance.goal.templateSelectorHint')}</HintText>
                <FieldGroup>
                  <GenericSelect
                    label={t('performance.goal.templateSelectorLabel')}
                    value={templateId}
                    onChange={applyTemplate}
                    options={templateOptions}
                    idKey="id"
                    labelKey="name"
                  />
                </FieldGroup>
              </>
            )}
            <FieldGroup>
              <GenericSelect
                label={t('performance.goal.fields.goalType')}
                value={form.goalType}
                onChange={(v) => handleFieldChange('goalType', v)}
                options={goalTypeOptions}
                idKey="id"
                labelKey="name"
              />
            </FieldGroup>
            <FieldGroup>
              <TextInput
                label={t('performance.goal.fields.title')}
                value={form.title}
                onChange={(e) => handleFieldChange('title', e.target.value)}
                required
              />
            </FieldGroup>
            {/* Plan 3, Task 7 — hedef ağırlığı; yalnız seçili dönemin usesGoalWeights'i
                true iken render edilir (parite: bayraksız dönemde alan HİÇ görünmez). */}
            {periodUsesGoalWeights && (
              <FieldGroup>
                <TextInput
                  data-role="goal-weight-pct"
                  label={t('performance.goal.fields.weight')}
                  type="number"
                  min={0}
                  max={100}
                  step="0.01"
                  value={form.weightPct}
                  onChange={(e) => handleFieldChange('weightPct', e.target.value)}
                />
              </FieldGroup>
            )}
            <FieldGroup>
              <TextInput
                label={t('performance.goal.fields.description')}
                type="textarea"
                rows={3}
                value={form.description}
                onChange={(e) => handleFieldChange('description', e.target.value)}
              />
            </FieldGroup>
            {/* P3 — hedef son tarihi (opsiyonel; boş = dönem bitişi). E04 "geciken hedef" (M12) buna bakar. */}
            <FieldGroup>
              <DateTimePicker
                label={t('performance.goal.fields.dueDate')}
                mode="date"
                value={form.dueDate}
                onChange={(v) => handleFieldChange('dueDate', v || null)}
                minDate={minDay}
                maxDate={maxDay}
              />
              <MutedHint>{t('performance.goal.dueDateHint')}</MutedHint>
            </FieldGroup>

            {/* P3 — kilometre taşı: adım editörü (ağırlık toplamı TAM 100). */}
            {form.goalType === 'milestone' && (
              <>
                <SectionTitle>{t('performance.goal.milestone.steps')}</SectionTitle>
                <MilestoneStepsEditor
                  value={form.milestoneSteps}
                  onChange={(v) => handleFieldChange('milestoneSteps', v)}
                  minDate={minDay}
                  maxDate={maxDay}
                />
              </>
            )}

            {/* P3 — eşik/ikili: koşul (gösterge + operatör + eşik değeri + birim). */}
            {form.goalType === 'threshold' && (
              <>
                <SectionTitle>{t('performance.goal.threshold.condition')}</SectionTitle>
                <FieldGroup>
                  <TextInput
                    label={t('performance.goal.threshold.indicator')}
                    value={form.kpiIndicatorName}
                    onChange={(e) => handleFieldChange('kpiIndicatorName', e.target.value)}
                    required
                  />
                </FieldGroup>
                <Row $cols={form.thresholdOperator === 'binary' ? '1fr' : '1fr 1fr 1fr'}>
                  <FieldGroup>
                    <GenericSelect
                      label={t('performance.goal.threshold.operatorLabel')}
                      value={form.thresholdOperator}
                      onChange={(v) => handleFieldChange('thresholdOperator', v)}
                      options={thresholdOperatorOptions}
                      idKey="id"
                      labelKey="name"
                    />
                  </FieldGroup>
                  {form.thresholdOperator !== 'binary' && (
                    <FieldGroup>
                      <TextInput
                        label={t('performance.goal.threshold.value')}
                        type="number"
                        value={form.thresholdValue}
                        onChange={(e) => handleFieldChange('thresholdValue', e.target.value)}
                      />
                    </FieldGroup>
                  )}
                  {form.thresholdOperator !== 'binary' && (
                    <FieldGroup>
                      <GenericSelect
                        label={t('performance.goal.kpiUnitLabel')}
                        value={form.kpiUnit}
                        onChange={(v) => handleFieldChange('kpiUnit', v)}
                        options={kpiUnitOptions}
                        idKey="id"
                        labelKey="name"
                      />
                    </FieldGroup>
                  )}
                </Row>
                <MutedHint>{t('performance.goal.threshold.hint')}</MutedHint>
              </>
            )}

            {form.goalType === 'okr' && (
              <>
                <SectionTitle>{t('performance.goal.keyResults')}</SectionTitle>
                {form.keyResults.map((kr, idx) => (
                  <KrRow key={kr._key} $cols={periodUsesGoalWeights ? '1fr 120px auto' : '1fr auto'}>
                    <TextInput
                      label={t('performance.goal.keyResultTitle')}
                      value={kr.title}
                      onChange={(e) => updateKrTitle(idx, e.target.value)}
                    />
                    {/* Plan 3, Task 7 — KR ağırlığı; opsiyonel, dönem bayrağına bağlı (parite). */}
                    {periodUsesGoalWeights && (
                      <TextInput
                        data-role={`kr-weight-${idx}`}
                        label={t('performance.goal.fields.weight')}
                        type="number"
                        min={0}
                        max={100}
                        step="0.01"
                        value={kr.weightPct}
                        onChange={(e) => updateKrWeight(idx, e.target.value)}
                      />
                    )}
                    <RemoveBtn type="button" onClick={() => removeKr(idx)} title={t('common.delete')}>×</RemoveBtn>
                  </KrRow>
                ))}
                <Button variant="outline" onClick={addKr} style={{ marginBottom: 8 }}>
                  + {t('performance.goal.addKeyResult')}
                </Button>
              </>
            )}
            {form.goalType === 'kpi' && (
              <Row $cols="1fr 1fr 1fr">
                <FieldGroup>
                  <TextInput
                    label={t('performance.goal.kpiIndicatorName')}
                    value={form.kpiIndicatorName}
                    onChange={(e) => handleFieldChange('kpiIndicatorName', e.target.value)}
                  />
                </FieldGroup>
                <FieldGroup>
                  <TextInput
                    label={t('performance.goal.kpiTargetValue')}
                    type="number"
                    value={form.kpiTargetValue}
                    onChange={(e) => handleFieldChange('kpiTargetValue', e.target.value)}
                  />
                </FieldGroup>
                <FieldGroup>
                  <GenericSelect
                    label={t('performance.goal.kpiUnitLabel')}
                    value={form.kpiUnit}
                    onChange={(v) => handleFieldChange('kpiUnit', v)}
                    options={kpiUnitOptions}
                    idKey="id"
                    labelKey="name"
                  />
                </FieldGroup>
              </Row>
            )}

            {/* Skorlama Faz F, Task 4 (task-2-report.md §1) — opsiyonel Baz
                Değer + Tavan %; yalnız KPI tipinde anlamlı (BE de yalnız
                GoalType=="kpi" iken saklıyor). Hedef Yönü (müşteri isteği
                2026-08-10): azalış hedefinde bazsız oran target/actual olur. */}
            {form.goalType === 'kpi' && (
              <Row $cols="1fr 1fr 1fr">
                <FieldGroup>
                  <GenericSelect
                    label={t('performance.goal.kpiDirectionLabel')}
                    value={form.kpiDirection}
                    onChange={(v) => handleFieldChange('kpiDirection', v)}
                    options={kpiDirectionOptions}
                    idKey="id"
                    labelKey="name"
                  />
                  {form.kpiDirection === 'decrease' && (
                    <HintText style={{ color: '#6c757d' }}>{t('performance.goal.kpiDirectionHint')}</HintText>
                  )}
                </FieldGroup>
                <FieldGroup>
                  <TextInput
                    label={t('performance.goal.kpiBaselineValue')}
                    type="number"
                    value={form.kpiBaselineValue}
                    onChange={(e) => handleFieldChange('kpiBaselineValue', e.target.value)}
                  />
                  <HintText style={{ color: '#6c757d' }}>{t('performance.goal.kpiBaselineValueHint')}</HintText>
                </FieldGroup>
                <FieldGroup>
                  <TextInput
                    label={t('performance.goal.kpiCapPct')}
                    type="number"
                    min={100}
                    max={300}
                    value={form.kpiCapPct}
                    onChange={(e) => handleFieldChange('kpiCapPct', e.target.value)}
                  />
                  <HintText style={{ color: '#6c757d' }}>{t('performance.goal.kpiCapPctHint')}</HintText>
                </FieldGroup>
              </Row>
            )}

            <Actions>
              <Button variant="outline" onClick={onClose}>{t('common.cancel')}</Button>
              <Button onClick={handleSave} disabled={!canSave || saving} loading={saving}>
                {t('common.save')}
              </Button>
              {goal && (
                <Button onClick={handleSubmit} disabled={!canSubmit || submitting} loading={submitting}>
                  {t('performance.goal.actions.submit')}
                </Button>
              )}
            </Actions>
          </>
        )}

        {isPendingApproval && (
          <>
            <ReadonlyValue><strong>{form.title}</strong></ReadonlyValue>
            {weightReadonly}
            {form.description && <ReadonlyValue>{form.description}</ReadonlyValue>}
            {goal.goalType === 'okr' && (
              (goal.keyResults || []).map((kr) => (
                <ReadonlyValue key={kr.id}>{kr.title}</ReadonlyValue>
              ))
            )}
            {goal.goalType === 'kpi' && (
              <ReadonlyValue>
                {goal.kpiIndicatorName} — {t('performance.goal.kpiTargetValue')}: {goal.kpiTargetValue}
                {goal.kpiDirection ? ` · ${t(`performance.goal.kpiDirection.${goal.kpiDirection}`)}` : ''}
              </ReadonlyValue>
            )}
            {/* P3 — yeni tipler salt-okunur. */}
            <GoalTypeDetails goal={goal} mode="readonly" />
            <Actions>
              <Button variant="outline" onClick={onClose}>{t('common.close')}</Button>
            </Actions>
          </>
        )}

        {isTracking && (
          <>
            <ReadonlyValue><strong>{form.title}</strong></ReadonlyValue>
            {weightReadonly}
            {/* P3 — kilometre taşı: adım "tamamlandı" bildirimi (anında kaydedilir); eşik: sonuç alanı. */}
            {goal.goalType === 'milestone' && (
              <GoalTypeDetails
                goal={goal}
                mode="owner"
                canAct={canActOnSteps}
                onSubmitStep={apiHooks.submitMilestoneStep}
                onChanged={reload}
              />
            )}
            {goal.goalType === 'threshold' && (
              <>
                <GoalTypeDetails goal={goal} mode="readonly" />
                {thresholdResultField}
              </>
            )}
            {goal.goalType === 'okr' && (
              <>
                <SectionTitle>{t('performance.goal.keyResults')}</SectionTitle>
                {(goal.keyResults || []).map((kr) => (
                  <KrRow key={kr.id} $cols="2fr 1fr">
                    <ReadonlyValue>{kr.title}</ReadonlyValue>
                    <TextInput
                      label={t('performance.goal.krProgress')}
                      type="number"
                      min={0}
                      max={100}
                      value={krProgressDraft[kr.id] ?? 0}
                      onChange={(e) => setKrProgressDraft((prev) => ({ ...prev, [kr.id]: e.target.value }))}
                    />
                  </KrRow>
                ))}
              </>
            )}
            {goal.goalType === 'kpi' && (
              <FieldGroup>
                <TextInput
                  label={t('performance.goal.kpiActualValue')}
                  type="number"
                  value={kpiActualDraft}
                  onChange={(e) => setKpiActualDraft(e.target.value)}
                />
              </FieldGroup>
            )}
            {/* Skorlama Faz F, Task 5 (task-1-report.md §1) — opsiyonel
                check-in notu; yazılan ilerleme/KPI güncellemesine eşlik eder.
                P3 — kilometre taşında adım aksiyonları anında kaydedilir (not/Kaydet yok). */}
            {goal.goalType !== 'milestone' && (
              <FieldGroup>
                <TextInput
                  data-role="progress-note"
                  label={t('goalCheckIns.progressNoteLabel')}
                  type="textarea"
                  rows={2}
                  value={progressNoteDraft}
                  onChange={(e) => setProgressNoteDraft(e.target.value)}
                />
              </FieldGroup>
            )}
            <Actions>
              <Button variant="outline" onClick={onClose}>{t('common.close')}</Button>
              {goal.goalType !== 'milestone' && (
                <Button onClick={handleSaveTracking} disabled={trackingBusy} loading={trackingBusy}>
                  {t('common.save')}
                </Button>
              )}
            </Actions>
          </>
        )}

        {isEvaluating && (
          <>
            <ReadonlyValue><strong>{form.title}</strong></ReadonlyValue>
            {weightReadonly}
            {/* P3 — dönem sonu: kilometre taşında kalan adımlar bildirilebilir (kabul yöneticide);
                eşikte final sonuç girilir. */}
            {goal.goalType === 'milestone' && (
              <GoalTypeDetails
                goal={goal}
                mode="owner"
                canAct={canActOnSteps}
                onSubmitStep={apiHooks.submitMilestoneStep}
                onChanged={reload}
              />
            )}
            {goal.goalType === 'threshold' && (
              <>
                <GoalTypeDetails goal={goal} mode="readonly" />
                {thresholdResultField}
              </>
            )}
            {goal.goalType === 'okr' && (
              <>
                <SectionTitle>{t('performance.goal.keyResults')}</SectionTitle>
                {(goal.keyResults || []).map((kr) => (
                  <KrRow key={kr.id} $cols="2fr 1fr 1fr">
                    <ReadonlyValue>{kr.title}</ReadonlyValue>
                    <GenericSelect
                      label={t('performance.goal.employeeScore')}
                      value={employeeScoresDraft[kr.id] ?? null}
                      onChange={(v) => setEmployeeScoresDraft((prev) => ({ ...prev, [kr.id]: v }))}
                      options={SCORE_OPTIONS}
                    />
                    <ScoreCompare>
                      {t('performance.goal.managerScore')}: {kr.managerScore ?? '-'}
                      {kr.managerComment ? ` (${kr.managerComment})` : ''}
                    </ScoreCompare>
                  </KrRow>
                ))}
              </>
            )}
            {goal.goalType === 'kpi' && (
              <FieldGroup>
                <TextInput
                  label={t('performance.goal.kpiActualValue')}
                  type="number"
                  value={kpiActualDraft}
                  onChange={(e) => setKpiActualDraft(e.target.value)}
                />
              </FieldGroup>
            )}
            <FieldGroup>
              <TextInput
                label={t('performance.goal.selfAssessment')}
                type="textarea"
                rows={3}
                value={selfAssessmentDraft}
                onChange={(e) => setSelfAssessmentDraft(e.target.value)}
              />
            </FieldGroup>
            {/* P1 — sapma gerekçesi (opsiyonel): OKR'de her zaman; KPI'da canlı
                tamamlanma <%100 iken (veya alan zaten doluyken — yazılmış metin
                tamamlanma 100'e çıkınca sessizce gizlenmesin). */}
            {(goal.goalType === 'okr'
              || goal.goalType === 'milestone'
              || goal.goalType === 'threshold'
              || deviationRationaleDraft !== ''
              || !(goal.kpiTargetValue > 0)
              || (Number(kpiActualDraft !== '' ? kpiActualDraft : goal.kpiActualValue ?? 0) / goal.kpiTargetValue) * 100 < 100) && (
              <FieldGroup>
                <TextInput
                  label={t('performance.goal.deviationRationale')}
                  type="textarea"
                  rows={2}
                  value={deviationRationaleDraft}
                  onChange={(e) => setDeviationRationaleDraft(e.target.value)}
                />
              </FieldGroup>
            )}
            <Actions>
              <Button variant="outline" onClick={onClose}>{t('common.close')}</Button>
              <Button onClick={handleSaveScores} disabled={scoringBusy} loading={scoringBusy}>
                {t('performance.goal.saveScores')}
              </Button>
              <Button
                onClick={handleApproveClosure}
                disabled={!canApproveClosure || closureBusy}
                loading={closureBusy}
              >
                {t('performance.goal.approveClosure')}
              </Button>
            </Actions>
            {!canApproveClosure && <HintText>{t(closureHintKey)}</HintText>}
          </>
        )}

        {isClosed && (
          <>
            <ReadonlyValue><strong>{form.title}</strong></ReadonlyValue>
            {weightReadonly}
            {goal.goalType === 'okr' && (
              (goal.keyResults || []).map((kr) => (
                <KrRow key={kr.id} $cols="2fr 1fr 1fr">
                  <ReadonlyValue>{kr.title}</ReadonlyValue>
                  <ScoreCompare>{t('performance.goal.employeeScore')}: {kr.employeeScore ?? '-'}</ScoreCompare>
                  <ScoreCompare>{t('performance.goal.managerScore')}: {kr.managerScore ?? '-'}</ScoreCompare>
                </KrRow>
              ))
            )}
            {goal.goalType === 'kpi' && (
              <ReadonlyValue>
                {t('performance.goal.kpiActualValue')}: {goal.kpiActualValue ?? '-'}
                {goal.completionPct != null ? ` (${t('performance.goal.kpiCompletionPct')}: ${goal.completionPct}%)` : ''}
              </ReadonlyValue>
            )}
            {/* P3 — yeni tipler: adımlar / koşul + sonuç (salt-okunur). */}
            <GoalTypeDetails goal={goal} mode="readonly" />
            {(goal.goalType === 'milestone' || goal.goalType === 'threshold') && goal.completionPct != null && (
              <ReadonlyValue>{`${t('performance.goal.kpiCompletionPct')}: ${goal.completionPct}%`}</ReadonlyValue>
            )}
            {goal.selfAssessmentText && (
              <ReadonlyValue>{t('performance.goal.selfAssessment')}: {goal.selfAssessmentText}</ReadonlyValue>
            )}
            {goal.deviationRationale && (
              <ReadonlyValue>{t('performance.goal.deviationRationale')}: {goal.deviationRationale}</ReadonlyValue>
            )}
            <ReadonlyValue>{t('performance.goal.closureApproved')}</ReadonlyValue>
            <Actions>
              <Button variant="outline" onClick={onClose}>{t('common.close')}</Button>
            </Actions>
          </>
        )}

        {/* Müşteri geri bildirimi (2026-10-28) — yukarıdaki dalların hiçbirine düşmeyen durumlar
            (terk edilmiş hedef; değerlendirme penceresi kapanmış "evaluating" hedef): salt-okunur
            özet + Kapat. Bu dal olmadan tam ekran diyalog kapatılamıyordu (bkz. hasStateBranch). */}
        {!hasStateBranch && (
          <>
            <ReadonlyValue><strong>{form.title}</strong></ReadonlyValue>
            {weightReadonly}
            {form.description && <ReadonlyValue>{form.description}</ReadonlyValue>}
            <HintText data-role="readonly-state-hint" style={{ color: '#6c757d' }}>
              {t(goal.status === ABANDONED_STATUS ? 'performance.goal.abandonedHint' : 'performance.goal.windowClosedHint')}
            </HintText>
            {goal.goalType === 'okr' && (
              (goal.keyResults || []).map((kr) => (
                <KrRow key={kr.id} $cols="2fr 1fr 1fr">
                  <ReadonlyValue>{kr.title}</ReadonlyValue>
                  <ScoreCompare>{t('performance.goal.employeeScore')}: {kr.employeeScore ?? '-'}</ScoreCompare>
                  <ScoreCompare>{t('performance.goal.managerScore')}: {kr.managerScore ?? '-'}</ScoreCompare>
                </KrRow>
              ))
            )}
            {goal.goalType === 'kpi' && (
              <ReadonlyValue>
                {goal.kpiIndicatorName} — {t('performance.goal.kpiTargetValue')}: {goal.kpiTargetValue}
                {goal.kpiActualValue != null ? ` · ${t('performance.goal.kpiActualValue')}: ${goal.kpiActualValue}` : ''}
              </ReadonlyValue>
            )}
            <GoalTypeDetails goal={goal} mode="readonly" />
            <Actions>
              <Button data-role="readonly-close-btn" variant="outline" onClick={onClose}>{t('common.close')}</Button>
            </Actions>
          </>
        )}

        {/* Revizyon isteği — flag: GoalRevisionEnabled. Yalnız approved/evaluating'de anlamlı
            (backend RequestRevisionAsync bu iki durumun dışını reddeder). */}
        {!isNew && settings?.goalRevisionEnabled && (goal.status === 'approved' || goal.status === 'evaluating') && (
          <>
            <SectionTitle>{t('performance.goal.actions.requestRevision')}</SectionTitle>
            {!revisionOpen ? (
              <Button variant="outline" onClick={openRevision}>{t('performance.goal.actions.requestRevision')}</Button>
            ) : (
              <>
                <HintText style={{ color: '#6c757d', marginBottom: 8 }}>{t('performance.goal.revisionChangesHint')}</HintText>
                <FieldGroup>
                  <TextInput
                    label={t('performance.goal.fields.title')}
                    value={revisionForm.title}
                    onChange={(e) => setRevisionForm((prev) => ({ ...prev, title: e.target.value }))}
                  />
                </FieldGroup>
                <FieldGroup>
                  <TextInput
                    label={t('performance.goal.fields.description')}
                    type="textarea"
                    rows={2}
                    value={revisionForm.description}
                    onChange={(e) => setRevisionForm((prev) => ({ ...prev, description: e.target.value }))}
                  />
                </FieldGroup>
                {/* Plan 3, Task 7 — hedef ağırlığı revizyonu; yalnız dönem bayrağı açıkken
                    (KR ağırlığı revizyon kapsamı DIŞINDA, bkz. handleRequestRevision yorumu). */}
                {periodUsesGoalWeights && (
                  <FieldGroup>
                    <TextInput
                      data-role="revision-weight-pct"
                      label={t('performance.goal.fields.weight')}
                      type="number"
                      min={0}
                      max={100}
                      step="0.01"
                      value={revisionForm.weightPct}
                      onChange={(e) => setRevisionForm((prev) => ({ ...prev, weightPct: e.target.value }))}
                    />
                  </FieldGroup>
                )}
                {/* P3 — son tarih revizyonu (tüm tipler). */}
                <FieldGroup>
                  <DateTimePicker
                    label={t('performance.goal.fields.dueDate')}
                    mode="date"
                    value={revisionForm.dueDate}
                    onChange={(v) => setRevisionForm((prev) => ({ ...prev, dueDate: v || null }))}
                    minDate={minDay}
                    maxDate={maxDay}
                  />
                </FieldGroup>
                {/* P3 — kilometre taşı: adım başlığı/ağırlığı/son tarihi (etkin toplam 100 olmalı). */}
                {goal.goalType === 'milestone' && (goal.milestoneSteps || []).map((st) => (
                  <KrRow key={st.id} $cols="2fr 100px 1fr">
                    <TextInput
                      label={t('performance.goal.milestone.step')}
                      value={revisionForm.steps[st.id]?.title || ''}
                      onChange={(e) => setRevisionForm((prev) => ({ ...prev, steps: { ...prev.steps, [st.id]: { ...prev.steps[st.id], title: e.target.value } } }))}
                    />
                    <TextInput
                      label={t('performance.goal.milestone.stepWeight')}
                      type="number"
                      value={revisionForm.steps[st.id]?.weightPct ?? ''}
                      onChange={(e) => setRevisionForm((prev) => ({ ...prev, steps: { ...prev.steps, [st.id]: { ...prev.steps[st.id], weightPct: e.target.value } } }))}
                    />
                    <DateTimePicker
                      label={t('performance.goal.milestone.stepDueDate')}
                      mode="date"
                      value={revisionForm.steps[st.id]?.dueDate || null}
                      onChange={(v) => setRevisionForm((prev) => ({ ...prev, steps: { ...prev.steps, [st.id]: { ...prev.steps[st.id], dueDate: v || null } } }))}
                      minDate={minDay}
                      maxDate={maxDay}
                    />
                  </KrRow>
                ))}
                {/* P3 — eşik: operatör + değer (uygulanınca yeni koşul sürümü; eskisi korunur). */}
                {goal.goalType === 'threshold' && (
                  <Row $cols="1fr 1fr">
                    <FieldGroup>
                      <GenericSelect
                        label={t('performance.goal.threshold.operatorLabel')}
                        value={revisionForm.thresholdOperator}
                        onChange={(v) => setRevisionForm((prev) => ({ ...prev, thresholdOperator: v }))}
                        options={thresholdOperatorOptions}
                        idKey="id"
                        labelKey="name"
                      />
                    </FieldGroup>
                    {revisionForm.thresholdOperator !== 'binary' && (
                      <FieldGroup>
                        <TextInput
                          label={t('performance.goal.threshold.value')}
                          type="number"
                          value={revisionForm.thresholdValue}
                          onChange={(e) => setRevisionForm((prev) => ({ ...prev, thresholdValue: e.target.value }))}
                        />
                      </FieldGroup>
                    )}
                  </Row>
                )}
                {goal.goalType === 'kpi' && (
                  <>
                    <FieldGroup>
                      <TextInput
                        label={t('performance.goal.kpiTargetValue')}
                        type="number"
                        value={revisionForm.kpiTargetValue}
                        onChange={(e) => setRevisionForm((prev) => ({ ...prev, kpiTargetValue: e.target.value }))}
                      />
                    </FieldGroup>
                    <FieldGroup>
                      <GenericSelect
                        label={t('performance.goal.kpiDirectionLabel')}
                        value={revisionForm.kpiDirection}
                        onChange={(v) => setRevisionForm((prev) => ({ ...prev, kpiDirection: v }))}
                        options={kpiDirectionOptions}
                        idKey="id"
                        labelKey="name"
                      />
                    </FieldGroup>
                    {/* Skorlama Faz F, Task 4 (task-2-report.md §5) — revizyon
                        beyaz listesine dahil edilen baz/tavan alanları. */}
                    <FieldGroup>
                      <TextInput
                        label={t('performance.goal.kpiBaselineValue')}
                        type="number"
                        value={revisionForm.kpiBaselineValue}
                        onChange={(e) => setRevisionForm((prev) => ({ ...prev, kpiBaselineValue: e.target.value }))}
                      />
                    </FieldGroup>
                    <FieldGroup>
                      <TextInput
                        label={t('performance.goal.kpiCapPct')}
                        type="number"
                        min={100}
                        max={300}
                        value={revisionForm.kpiCapPct}
                        onChange={(e) => setRevisionForm((prev) => ({ ...prev, kpiCapPct: e.target.value }))}
                      />
                    </FieldGroup>
                  </>
                )}
                {goal.goalType === 'okr' && (
                  (goal.keyResults || []).map((kr) => (
                    <FieldGroup key={kr.id}>
                      <TextInput
                        label={kr.title}
                        value={revisionForm.krTitles[kr.id] || ''}
                        onChange={(e) => setRevisionForm((prev) => ({ ...prev, krTitles: { ...prev.krTitles, [kr.id]: e.target.value } }))}
                      />
                    </FieldGroup>
                  ))
                )}
                <Actions>
                  <Button variant="outline" onClick={() => setRevisionOpen(false)}>{t('common.cancel')}</Button>
                  <Button onClick={handleRequestRevision} disabled={revisionBusy} loading={revisionBusy}>
                    {t('common.save')}
                  </Button>
                </Actions>
              </>
            )}
          </>
        )}

        {/* Üst hedef — flag: CascadingGoalsEnabled. Yalnız zaten oluşturulmuş hedeflerde
            (SetParentGoalAsync ayrı bir uç, yeni hedef oluşturma gövdesinin parçası değil). */}
        {!isNew && settings?.cascadingGoalsEnabled && (
          <>
            <SectionTitle>{t('performance.goal.parentGoal')}</SectionTitle>
            {/* #73 — AsyncGenericSelect'in shrink durumundaki floating label'ı
                (FixedLabel, position:absolute; top:-8px; OuterWrapper'a göre)
                hemen üstteki HintText'i kapatıyordu: HintText'in kendi stili
                yalnız margin-top:4px taşıyor, margin-bottom YOK — Row'un ilk
                çocuğu (seçici) ile hint metni arasında boşluk kalmıyordu.
                Aynı dosyada revisionChangesHint (satır ~997) AYNI sorunu
                marginBottom:8 inline override'ıyla çözmüştü — o desen
                buraya da uygulandı (komşu bölüm örneği). */}
            <HintText style={{ color: '#6c757d', marginBottom: 8 }}>{t('performance.goal.parentGoalHint')}</HintText>
            <Row $cols="1fr auto">
              <AsyncGenericSelect
                label={t('performance.goal.parentGoal')}
                value={parentGoalId}
                onChange={setParentGoalId}
                onSearch={parentSearchProps.onSearch}
                onGetById={parentSearchProps.onGetById}
              />
              <Button onClick={handleSaveParent} disabled={parentBusy} loading={parentBusy}>
                {t('common.save')}
              </Button>
            </Row>
          </>
        )}

        {/* Skorlama Plan 4, Task 4 (task-4-brief.md) — "Üst Hedefe Hizala" (OrgGoal
            hizalaması). Üst hedef (cascading, bireysel↔bireysel) bölümünden AYRI bir
            kavram — bu, bireysel hedefin bir KURUMSAL (company/department/team)
            hedefe bağlanması. Bayraksız (task-3-report.md — SetAlignedOrgGoalAsync'te
            settings flag'i yok); yalnız goal TERMİNAL DEĞİLKEN gösterilir (backend'in
            TEK statü reddi — task-3-report.md karar #2, parentGoal'ın flag koşulunun
            YERİNE terminal-guard kullanılıyor).
            PMB-01 (2026-09-08): kapı `isClosed`ten `isTerminal`e genişletildi —
            hiç onaylanmamış hedef kapanışta artık "closed" değil "abandoned" olduğu
            için bu bölüm terk edilmiş hedeflerde yeniden AÇILMIŞTI; backend
            SetAlignedOrgGoalAsync'in IsPeriodLive kapısı zaten 409 döndürdüğünden
            ölü bir kontroldü. */}
        {!isNew && !isTerminal && (
          <>
            <SectionTitle>{t('performance.goal.alignedOrgGoalSectionTitle')}</SectionTitle>
            {/* #73 fix'in aynısı — bu bölüm "Üst Hedef" ile BİREBİR AYNI
                SectionTitle/HintText/Row/AsyncGenericSelect iskeletini
                paylaşıyor, dolayısıyla AYNI shrink-label çakışmasını taşıyordu
                (bkz. yukarıdaki parentGoalHint yorumu) — tutarlılık için
                burada da düzeltildi. */}
            <HintText style={{ color: '#6c757d', marginBottom: 8 }}>{t('performance.goal.alignedOrgGoalHint')}</HintText>
            <Row $cols="1fr auto">
              <AsyncGenericSelect
                label={t('performance.goal.alignedOrgGoalSectionTitle')}
                value={alignedOrgGoalId}
                onChange={setAlignedOrgGoalId}
                onSearch={alignedOrgGoalSearchProps.onSearch}
                onGetById={alignedOrgGoalSearchProps.onGetById}
              />
              <Button data-role="save-aligned-org-goal-btn" onClick={handleSaveAlignedOrgGoal} disabled={alignBusy} loading={alignBusy}>
                {t('common.save')}
              </Button>
            </Row>
          </>
        )}

        {/* Skorlama Faz F, Task 5 (spec §4) — "İlerleme Geçmişi". Yalnız
            check-in üretilebilecek durumlarda (bkz. CHECK_IN_HISTORY_STATUSES
            yorumu); sahip her zaman /my ucunu kullanır — bu uçta 403 pratikte
            olmaz (goal zaten kendisininse), yalnız hedef-bulunamadı yolu var
            (task-1-report.md §3). */}
        {!isNew && CHECK_IN_HISTORY_STATUSES.includes(goal.status) && (
          <GoalCheckInHistory goalId={goal.id} apiPath={`/v1/period-goals/my/${goal.id}/check-ins`} />
        )}
      </ModalCard>
    </Modal>
  );
}
